import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { computed, inject, Signal, Type } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  BackendResponse,
  DocumentReferenceBase,
  DocumentReferenceEntityType,
} from '@qtek/shared/models';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  DocumentServingRequestOptions,
  DocumentServingService,
} from '../../document-serving.service';
import { DocumentReferenceService } from './document-reference.service';

/** Options for a reference store's `upload()`. */
export interface DocumentUploadOptions extends DocumentServingRequestOptions {
  /** The upload's percentage (0-100) as bytes go out. */
  onProgress?: (percent: number) => void;
}
import {
  applyReferenceRemoval,
  applyReferenceUpsert,
  applyScopeEviction,
  createInitialDocumentReferenceState,
  DocumentReferenceQuery,
  DocumentReferenceSelector,
  DocumentReferenceState,
  filterReferences,
  normalizeQuery,
  orderReferences,
  reconcileReferences,
  selectorKey,
  stampEntityType,
} from './document-reference.updaters';

/**
 * Reusable signalStore feature shared by all 6 DocumentReference entities.
 * Each concrete store just plugs in its own service token (typed to its own
 * entity-specific response shape) — no per-entity CRUD/upload logic to
 * duplicate or drift.
 *
 * State is keyed by the reference row's own `_id` (see
 * `document-reference.updaters.ts`), so loads for different entities/
 * attributes accumulate side by side instead of overwriting each other. Reads
 * are scoped through `referencesFor`/`latestFor` rather than by each consumer
 * filtering a shared array.
 *
 * Backend responses don't include `entityType` (see `DocumentReferenceBase`
 * in `document-reference.model.ts`), so `load`/`upload` stamp it on
 * client-side from `service.entityType` before anything reaches state.
 */
export function withDocumentReferenceMethods<
  T extends DocumentReferenceBase & { entityType: DocumentReferenceEntityType },
>(serviceType: Type<DocumentReferenceService<T>>) {
  return signalStoreFeature(
    withState<DocumentReferenceState<T>>(
      createInitialDocumentReferenceState<T>()
    ),
    withComputed(({ referenceMap, pending }) => ({
      /** Every cached reference, oldest first. */
      references: computed(() => orderReferences(referenceMap())),
      isLoading: computed(() => pending() > 0),
    })),
    withMethods(
      (
        store,
        service = inject(serviceType),
        documentServingService = inject(DocumentServingService)
      ) => {
        /**
         * Per-store-instance caches — deliberately not module scope, since
         * this feature backs 6 separate stores and module-level state would
         * leak between them.
         */
        const inFlight = new Map<string, Observable<readonly T[]>>();
        /** query key -> generation counter. See `fetch`'s supersede-guard. */
        const generations = new Map<string, number>();
        const lastResponseIds = new Map<string, readonly string[]>();
        const selectorCache = new Map<string, Signal<readonly T[]>>();
        const latestCache = new Map<string, Signal<T | null>>();
        const byIdCache = new Map<string, Signal<T | null>>();

        /**
         * Identity comparator: keeps the previous array when the slice's
         * members are unchanged, so a reload that returns the same data
         * doesn't re-fire consumer `computed`s and `effect`s.
         */
        const sameReferences = (a: readonly T[], b: readonly T[]): boolean =>
          a === b ||
          (a.length === b.length &&
            a.every((reference, index) => reference === b[index]));

        const referencesFor = (
          selector: DocumentReferenceSelector
        ): Signal<readonly T[]> => {
          const key = selectorKey(selector);
          let cached = selectorCache.get(key);

          if (!cached) {
            cached = computed(
              () => filterReferences(store.references(), selector),
              { equal: sameReferences }
            );
            selectorCache.set(key, cached);
          }

          return cached;
        };

        const latestFor = (
          selector: DocumentReferenceSelector
        ): Signal<T | null> => {
          const key = selectorKey(selector);
          let cached = latestCache.get(key);

          if (!cached) {
            const slice = referencesFor(selector);
            cached = computed(() => slice().at(-1) ?? null);
            latestCache.set(key, cached);
          }

          return cached;
        };

        const referenceById = (referenceId: string): Signal<T | null> => {
          let cached = byIdCache.get(referenceId);

          if (!cached) {
            cached = computed(() => store.referenceMap()[referenceId] ?? null);
            byIdCache.set(referenceId, cached);
          }

          return cached;
        };

        const fetch = (
          input: DocumentReferenceQuery
        ): Observable<readonly T[]> => {
          const query = normalizeQuery(input);

          // Identical query already on the wire — share it rather than
          // issuing a second request. Required because `load` uses
          // `mergeMap` (see below), which no longer cancels the previous
          // call the way `switchMap` used to. A forced query skips this: it
          // exists precisely because a request already in flight may have
          // been issued under stale conditions (e.g. the caller's role just
          // changed) and must not be allowed to satisfy this one.
          if (!query.force) {
            const existing = inFlight.get(query.key);
            if (existing) {
              return existing;
            }
          }

          patchState(store, state => ({ pending: state.pending + 1 }));

          // Supersede-guard: a forced load can run alongside an older
          // request already on the wire for the same key. If the older one
          // settles after this one, its (possibly stale) response must not
          // overwrite what this request committed.
          const generation = (generations.get(query.key) ?? 0) + 1;
          generations.set(query.key, generation);

          const request$ = service.list(query.attributes, query.entityId).pipe(
            map(({ res }) => stampEntityType(res ?? [], service.entityType)),
            tap(incoming => {
              if (generations.get(query.key) !== generation) {
                return;
              }

              const result = reconcileReferences(
                store.referenceMap(),
                lastResponseIds.get(query.key) ?? [],
                incoming as readonly T[],
                query.force
              );
              lastResponseIds.set(query.key, result.ids);

              if (result.changed) {
                patchState(store, state => ({
                  referenceMap: result.referenceMap,
                  cacheBuster: state.cacheBuster + 1,
                }));
              }
            }),
            catchError(() => of([] as readonly T[])),
            finalize(() => {
              // Only clear the shared slot if it's still this request — an
              // older, superseded request's teardown must not evict the
              // newer (forced) request's in-flight entry.
              if (inFlight.get(query.key) === request$) {
                inFlight.delete(query.key);
              }
              patchState(store, state => ({ pending: state.pending - 1 }));
            }),
            shareReplay({ bufferSize: 1, refCount: false })
          ) as Observable<readonly T[]>;

          inFlight.set(query.key, request$);
          // `shareReplay` is cold until subscribed; kick it off so the
          // request is unconditional and late subscribers replay the result.
          request$.subscribe();

          return request$;
        };

        /**
         * Shared by `fetchById`/`update`: stamp `entityType` on the single
         * returned reference and upsert it into `referenceMap`. Both GET and
         * PUT return the full row (confirmed against the backend), so a
         * wholesale replace is correct for either. Neither touches
         * `lastResponseIds` — like `upload()`, these commit one row
         * directly, with no list-query scope to reconcile against. Errors
         * are not swallowed here (unlike `fetch`'s dedup-driven catchError)
         * — callers use the `{ next, error }` subscribe form, same as
         * `upload`/`remove`.
         */
        const commitSingle = (
          response$: Observable<BackendResponse<T>>
        ): Observable<T | null> =>
          response$.pipe(
            map(({ res }): T | null =>
              res ? ({ ...res, entityType: service.entityType } as T) : null
            ),
            tap(reference => {
              if (reference) {
                patchState(store, state =>
                  applyReferenceUpsert(state, reference)
                );
              }
            })
          );

        return {
          referencesFor,
          latestFor,
          referenceById,

          /**
           * `mergeMap`, not `switchMap`: concurrent loads for different
           * queries must both complete (two item widgets with different
           * `entityId`s no longer cancel each other). Identical concurrent
           * queries are de-duplicated inside `fetch`.
           */
          load: rxMethod<DocumentReferenceQuery>(
            pipe(mergeMap(query => fetch(query)))
          ),

          /** Issues the same de-duplicated request, but yields the result. */
          fetch,

          /**
           * Drops this selector's cached rows locally — no request, nothing
           * deleted server-side. For a consumer tearing down (see
           * `DataRoomComponent.ngOnDestroy`) so a root-provided store doesn't
           * accumulate every entity ever visited.
           */
          evict(selector: DocumentReferenceSelector): void {
            const key = selectorKey(selector);
            let evictedIds: readonly string[] = [];

            patchState(store, state => {
              const result = applyScopeEviction(state, selector);
              evictedIds = result.evictedIds;
              return result.state;
            });

            if (!evictedIds.length) return;

            // Same ledger fix-up `remove` does below: an id left in
            // `lastResponseIds` would make the next reconcile for that key
            // try to evict a row that is already gone.
            const dropped = new Set(evictedIds);
            lastResponseIds.forEach((ids, entryKey) => {
              const next = ids.filter(candidate => !dropped.has(candidate));
              if (next.length !== ids.length) {
                lastResponseIds.set(entryKey, next);
              }
            });
            lastResponseIds.delete(key);

            // Per-id memo; a stale entry outlives its reference forever
            // otherwise.
            evictedIds.forEach(id => byIdCache.delete(id));
          },

          remove(id: string): Observable<void> {
            return service.delete(id).pipe(
              tap(() => {
                lastResponseIds.forEach((ids, key) => {
                  if (ids.includes(id)) {
                    lastResponseIds.set(
                      key,
                      ids.filter(candidate => candidate !== id)
                    );
                  }
                });
                patchState(store, state => applyReferenceRemoval(state, id));
              }),
              map((): void => undefined)
            );
          },

          /**
           * `POST /api/v2/model/<Entity>DocumentReference` — creates a
           * reference row directly, no upload bytes (e.g. a placeholder
           * with no `document` yet).
           */
          create(payload: Partial<T>): Observable<T | null> {
            return commitSingle(service.post(payload));
          },

          /** Fetches one reference fresh from the server by its own `_id`. */
          fetchById(id: string): Observable<T | null> {
            return commitSingle(service.get(id));
          },

          /**
           * Updates a reference's `attribute`/`description` (the only
           * scalar fields on `DocumentReferenceBase` caller-editable through
           * this row — `document` is the nested file, managed elsewhere;
           * `entityId`/audit fields aren't).
           */
          update(
            id: string,
            payload: Partial<Pick<T, 'attribute' | 'description'>>
          ): Observable<T | null> {
            return commitSingle(service.put(payload, id));
          },

          /**
           * `formData` must match the upload contract (see
           * {@link buildDocumentReferenceFormData}). `commitWhen` lets a
           * caller defer the state commit behind an async check (e.g. avatar's
           * virus-scan wait) — resolving `false` discards the reference.
           * Defaults to committing immediately. `options.onProgress` gets the
           * upload's percentage (0-100) as bytes go out;
           * `options.skipErrorHandling` is for a caller that shows the failure
           * itself (see {@link DocumentServingRequestOptions}).
           */
          upload(
            formData: FormData,
            commitWhen: (reference: T) => Observable<boolean> = () => of(true),
            options: DocumentUploadOptions = {}
          ): Observable<T | null> {
            const { onProgress, ...requestOptions } = options;
            return documentServingService
              .uploadTokenless(formData, requestOptions)
              .pipe(
                tap((event: HttpEvent<unknown>) => {
                  if (
                    onProgress &&
                    event.type === HttpEventType.UploadProgress &&
                    event.total
                  ) {
                    onProgress(
                      Math.min(
                        100,
                        Math.round((event.loaded / event.total) * 100)
                      )
                    );
                  }
                }),
                filter(
                  (event: HttpEvent<unknown>) =>
                    event.type === HttpEventType.Response
                ),
                map((event): T | null => {
                  const body = (event as HttpResponse<unknown>).body as
                    | { res: T }
                    | T
                    | null;
                  if (!body) {
                    return null;
                  }
                  const reference = ('res' in body ? body.res : body) as T;
                  return {
                    ...reference,
                    entityType: service.entityType,
                  } as T;
                }),
                switchMap(reference => {
                  if (!reference) {
                    return of(null);
                  }
                  return commitWhen(reference).pipe(
                    map(commit => {
                      if (!commit) {
                        return null;
                      }
                      patchState(store, state =>
                        applyReferenceUpsert(state, reference)
                      );
                      return reference;
                    })
                  );
                })
              );
          },
        };
      }
    )
  );
}
